import React, { useRef } from "react";
import clsx from "clsx";

import Modal from "../../../atoms/Modal";
import ModalDeleteConfirm from "../../../molecules/ModalDeleteConfirm";
import { hasUnsavedTextareaInput } from "../../../libs/domUtils";
import { MODAL_MESSAGES } from "../../../constants/modalMessages";
import AnswerList from "../AnswerList";
import {
  Context,
  updateComment,
  createComment,
  deleteComment,
  toggleComment,
  fetchAnswers,
  fetchStudents,
  fetchGroups,
  fetchClasses,
  answerOpened,
  answerClosed,
  nameOpened,
  nameClosed,
  fetchAnswersFromOtherSchools,
  sendLike,
  cancelLike,
  clearAnswers,
} from "../../../teacherStore";
import useWaitApiCall from "../../../hooks/useWaitApiCall";

import styles from "./ModalAnswers.module.scss";

export type Props = {
  issueID: number;
  classIDs: number[];
  roundNo?: number;
  is_display_all_schools_answer: boolean;
  isMaterial?: boolean;
  onClose: () => void;
};

const ModalAnswers: React.FC<Props> = ({
  issueID,
  classIDs,
  roundNo = 1,
  is_display_all_schools_answer,
  isMaterial = false,
  onClose,
}) => {
  const { state, dispatch } = React.useContext(Context);
  const [calling, peep] = useWaitApiCall(state);
  const [showConfirmModal, setShowConfirmModal] = React.useState(false);
  const modalContentRef = useRef<HTMLDivElement>(null);

  const handleClose = React.useCallback(() => {
    if (hasUnsavedTextareaInput(modalContentRef)) {
      setShowConfirmModal(true);
    } else {
      onClose();
    }
  }, [onClose]);
  const classIDsStr = classIDs.join(",");
  const clsIDs = React.useMemo(
    () => classIDsStr.split(",").map(s => Number(s)),
    [classIDsStr],
  );

  const refreshAnswers = React.useCallback(() => {
    issueID && dispatch(fetchAnswers(issueID, clsIDs, roundNo));
  }, [clsIDs, dispatch, issueID, roundNo]);

  const answersWithIndex = React.useMemo(() => {
    return state.answersByClasses.map(answersByClass => {
      const answers = answersByClass.answers.map((answer, index) => {
        return { ...answer, index: index + 1 };
      });
      return { ...answersByClass, answers };
    });
  }, [state.answersByClasses]);

  React.useEffect(() => {
    if (is_display_all_schools_answer) {
      dispatch(fetchAnswersFromOtherSchools(issueID, clsIDs));
    }
  }, [clsIDs, dispatch, is_display_all_schools_answer, issueID]);

  React.useEffect(() => {
    dispatch(fetchStudents(clsIDs));
    dispatch(fetchGroups(clsIDs));
    dispatch(fetchClasses());
  }, [clsIDs, dispatch]);

  React.useEffect(() => {
    dispatch(clearAnswers());
    refreshAnswers();
    const refetchInterval = setInterval(() => refreshAnswers(), 10000);
    return () => clearInterval(refetchInterval);
  }, [dispatch, refreshAnswers]);

  const changeAnswerOpenClose = React.useCallback(
    (class_id: number, opened: boolean) => {
      if (opened) {
        dispatch(answerOpened(issueID, class_id, roundNo));
      } else {
        dispatch(answerClosed(issueID, class_id, roundNo));
      }
    },
    [dispatch, issueID, roundNo],
  );

  const changeNameOpenClose = React.useCallback(
    (class_id: number, opened: boolean) => {
      if (opened) {
        dispatch(nameOpened(issueID, class_id, roundNo));
      } else {
        dispatch(nameClosed(issueID, class_id, roundNo));
      }
    },
    [dispatch, issueID, roundNo],
  );

  const commentFunc = React.useCallback(
    (content: string, answer_id: number, comment_id?: number) => {
      comment_id
        ? dispatch(peep(updateComment(answer_id, content, comment_id)))
        : dispatch(peep(createComment(answer_id, content)));
    },
    [dispatch, peep],
  );

  const deleteCommentFunc = React.useCallback(
    (answer_id: number, comment_id: number) => {
      dispatch(peep(deleteComment(answer_id, comment_id)));
    },
    [dispatch, peep],
  );

  const toggleCommentFunc = React.useCallback(
    (answer_id: number, comment_id: number, is_hidden: boolean) => {
      dispatch(peep(toggleComment(answer_id, comment_id, is_hidden)));
    },
    [dispatch, peep],
  );

  const handleLike = React.useCallback(
    (answerId: number) => {
      const answer = state.answersByClasses
        .flatMap(c => c.answers)
        .find(a => a.id === answerId);
      if (!answer) return;
      answer.is_like
        ? dispatch(peep(cancelLike(answerId)))
        : dispatch(peep(sendLike(answerId)));
    },
    [dispatch, peep, state.answersByClasses],
  );

  return (
    <Modal
      className={clsx(styles.modal, isMaterial && "ms-0")}
      onClose={handleClose}
      transparentBackDrop={false}
    >
      <div className={styles.modal__content} ref={modalContentRef}>
        <AnswerList
          calling={calling}
          currentTeacherID={state.teacher.id}
          students={state.students}
          answersByClasses={answersWithIndex}
          answersOtherSchoolsByClasses={state.answersOtherSchoolsByClasses}
          classes={state.classes}
          classIDs={clsIDs}
          groups={state.groups}
          is_display_all_schools_answer={is_display_all_schools_answer}
          handleLike={handleLike}
          handleComment={commentFunc}
          handleDeleteComment={deleteCommentFunc}
          handleToggleComment={toggleCommentFunc}
          changeAnswerOpenClose={changeAnswerOpenClose}
          changeNameOpenClose={changeNameOpenClose}
        />
      </div>
      {showConfirmModal && (
        <ModalDeleteConfirm
          title={MODAL_MESSAGES.DISCARD_EDIT}
          onConfirm={() => {
            setShowConfirmModal(false);
            onClose();
          }}
          onClose={() => setShowConfirmModal(false)}
        />
      )}
    </Modal>
  );
};

export default ModalAnswers;
